import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useNetwork } from "@/lib/contexts/NetworkContext";
import { ChevronDown, Globe } from "lucide-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { convertSparkAddressToNetwork } from "@/lib/spark/address";

export function NetworkSwitcher() {
  const { network, setNetwork } = useNetwork();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  const networks: { label: string; value: string }[] = [
    {
      label: "Mainnet",
      value: "mainnet",
    },
    {
      label: "Regtest",
      value: "regtest",
    },
  ];

  const handleNetworkChange = (newNetwork: string) => {
    // Check if we're on an account page
    const accountPathMatch = pathname.match(/^\/address\/([^/]+)/);

    if (accountPathMatch?.[1]) {
      const currentSparkAddress = accountPathMatch[1];

      // Convert the address to the new network
      const convertedAddress = convertSparkAddressToNetwork(
        currentSparkAddress,
        newNetwork.toLowerCase() as "mainnet" | "regtest"
      );

      // If conversion was successful, redirect to the new address with the new network
      if (convertedAddress && convertedAddress !== currentSparkAddress) {
        // Update the URL with both the new address and network parameter
        const current = new URLSearchParams(Array.from(searchParams.entries()));
        current.set("network", newNetwork);
        const search = current.toString();
        const query = search ? `?${search}` : "";
        router.push(`/address/${convertedAddress}${query}`);
        return;
      }
    }

    // For all other cases (non-address pages or no conversion needed), just update the network
    setNetwork(newNetwork);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1">
          <Globe className="h-4 w-4" />
          <span className="capitalize">{network}</span>
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {networks.map((item) => (
          <DropdownMenuItem
            key={item.value}
            onClick={() => handleNetworkChange(item.value)}
            className="capitalize">
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
